import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LogOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { AppView } from '../../../types';
import BrightforgeMark from '../../BrightforgeMark';
import { Tooltip } from '../ui/Tooltip';
import { PixlieDoodle, type DoodleName } from '../../doodles';
import {
  avatarInitial,
  avatarPastelFor,
  formatPlanLabel,
  resolveDisplayName,
} from '../../../utils/userDisplay';

interface SidebarV2Props {
  currentView: AppView;
  onChangeView: (view: AppView) => void;
  queueCount?: number;
  planName?: string;
  userEmail?: string | null;
  userDisplayName?: string | null;
  onSignOut?: () => Promise<void> | void;
}

const SidebarV2: React.FC<SidebarV2Props> = ({
  currentView,
  onChangeView,
  queueCount = 0,
  planName = 'Free',
  userEmail = null,
  userDisplayName = null,
  onSignOut,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);

  const nickname = resolveDisplayName(userDisplayName, userEmail);
  const initial = avatarInitial(nickname);
  const pastel = avatarPastelFor(userEmail || userDisplayName || nickname);
  const planLabel = formatPlanLabel(planName);

  const coreNav: {
    id: AppView;
    label: string;
    doodle: DoodleName;
    path: string;
  }[] = [
    { id: AppView.HOME, label: 'Home', doodle: 'lens', path: '/dashboard' },
    { id: AppView.CREATE, label: 'Create', doodle: 'sparkCut', path: '/create' },
    { id: AppView.EDITOR, label: 'Editor', doodle: 'frames', path: '/editor' },
    { id: AppView.LIBRARY, label: 'Library', doodle: 'promptReel', path: '/library' },
    { id: AppView.WORKFLOWS, label: 'Workflows', doodle: 'thread', path: '/workflows' },
  ];

  const utilityNav: {
    id: AppView;
    label: string;
    doodle: DoodleName;
    path: string;
  }[] = [
    { id: AppView.USAGE, label: 'Usage', doodle: 'waveform', path: '/usage' },
    { id: AppView.BILLING, label: 'Billing', doodle: 'dolly', path: '/billing' },
    { id: AppView.SETTINGS, label: 'Settings', doodle: 'faceLock', path: '/settings' },
  ];

  const handleNavigation = (view: AppView, path: string) => {
    onChangeView(view);
    if (window.location.pathname !== path) navigate(path);
  };

  const handleLogout = async () => {
    try {
      await onSignOut?.();
      navigate('/login', { replace: true });
    } catch {
      navigate('/login', { replace: true });
    }
  };

  /** Expanded ~½ of former w-64 — tight to labels, not a fat empty column */
  const widthClass = expanded ? 'w-32' : 'w-[4.75rem]';

  const AvatarCircle = ({ sizeClass }: { sizeClass: string }) => (
    <div
      className={`${sizeClass} flex shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white`}
      style={{ backgroundColor: pastel }}
      aria-hidden
    >
      {initial}
    </div>
  );

  return (
    <aside
      className={[
        'relative flex h-screen shrink-0 flex-col border-r border-[var(--sidebar-border)]',
        'bg-[var(--sidebar)] text-[var(--sidebar-foreground)]',
        'transition-all duration-[var(--duration-slow)] ease-[var(--ease-out)]',
        widthClass,
      ].join(' ')}
    >
      <div className={`flex h-14 items-center ${expanded ? 'justify-between px-2' : 'justify-center gap-0 px-1'}`}>
        <div className={`flex items-center ${expanded ? 'gap-1.5 min-w-0' : ''}`}>
          <BrightforgeMark
            className="h-7 w-7 shrink-0"
            tileClassName="bg-[var(--foreground)]"
            glyphClassName="text-[var(--background)]"
            outlineClassName="border-[var(--border-subtle)] shadow-raised"
          />
          {expanded && (
            <span className="truncate text-sm font-semibold tracking-tight text-[var(--text-primary)]">
              PIXLIE
            </span>
          )}
        </div>
        <button
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          onClick={() => setExpanded((v) => !v)}
          className={`flex h-7 w-7 items-center justify-center rounded-md text-[var(--text-muted)] transition-colors hover:bg-[var(--sidebar-accent)] hover:text-[var(--text-primary)] ${expanded ? '' : 'absolute right-0.5 top-3.5'}`}
        >
          {expanded ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
      </div>

      <nav className={`flex-1 py-2 ${expanded ? 'px-1.5' : 'px-2'}`}>
        {expanded ? (
          <>
            <p className="px-2 pb-1 pt-1 text-[9px] font-medium uppercase tracking-widest text-[var(--text-muted)]">
              Create
            </p>
            {coreNav.map((item) => {
              const active = currentView === item.id;
              const showQueueBadge = item.id === AppView.HOME && queueCount > 0;
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  onClick={() => handleNavigation(item.id, item.path)}
                  className={[
                    'group relative flex items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-xs font-medium transition-all duration-[var(--duration-normal)]',
                    active
                      ? 'border border-[var(--border-subtle)] bg-[var(--sidebar-accent)] text-[var(--text-primary)] shadow-raised'
                      : 'border border-transparent text-[var(--text-muted)] hover:bg-[var(--sidebar-accent)]/60 hover:text-[var(--text-primary)]',
                  ].join(' ')}
                >
                  {active && (
                    <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-[var(--foreground)]" />
                  )}
                  <PixlieDoodle
                    name={item.doodle}
                    size="nav"
                    className="shrink-0 opacity-85 group-hover:opacity-100"
                  />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {showQueueBadge && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] tabular-nums text-[var(--text-muted)]">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                      {queueCount}
                    </span>
                  )}
                </NavLink>
              );
            })}

            <p className="px-2 pb-1 pt-3 text-[9px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              Account
            </p>
            {utilityNav.map((item) => {
              const active = currentView === item.id;
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  onClick={() => handleNavigation(item.id, item.path)}
                  className={[
                    'group relative flex items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-xs font-medium transition-all',
                    active
                      ? 'border border-[var(--border-subtle)] bg-[var(--sidebar-accent)] text-[var(--text-primary)] shadow-raised'
                      : 'border border-transparent text-[var(--text-muted)] hover:bg-[var(--sidebar-accent)]/60 hover:text-[var(--text-primary)]',
                  ].join(' ')}
                >
                  {active && (
                    <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-[var(--foreground)]" />
                  )}
                  <PixlieDoodle
                    name={item.doodle}
                    size="nav"
                    className="shrink-0 opacity-85 group-hover:opacity-100"
                  />
                  <span className="min-w-0 truncate">{item.label}</span>
                </NavLink>
              );
            })}
          </>
        ) : (
          <div className="flex flex-col items-center gap-1.5 px-1">
            {coreNav.map((item) => {
              const active = currentView === item.id;
              return (
                <Tooltip key={item.id} side="right" content={item.label}>
                  <button
                    onClick={() => handleNavigation(item.id, item.path)}
                    className={[
                      'flex h-11 w-11 items-center justify-center rounded-xl transition-all',
                      active
                        ? 'border border-[var(--border-subtle)] bg-[var(--sidebar-accent)] text-[var(--text-primary)] shadow-raised'
                        : 'text-[var(--text-muted)] hover:bg-[var(--sidebar-accent)]/60 hover:text-[var(--text-primary)]',
                    ].join(' ')}
                    aria-label={item.label}
                  >
                    <PixlieDoodle
                      name={item.doodle}
                      size="nav"
                      className={active ? 'opacity-100' : 'opacity-85'}
                    />
                  </button>
                </Tooltip>
              );
            })}
            <div className="my-1.5 h-px w-8 bg-[var(--border-subtle)]" />
            {utilityNav.map((item) => {
              const active = currentView === item.id;
              return (
                <Tooltip key={item.id} side="right" content={item.label}>
                  <button
                    onClick={() => handleNavigation(item.id, item.path)}
                    className={[
                      'flex h-11 w-11 items-center justify-center rounded-xl transition-all',
                      active
                        ? 'border border-[var(--border-subtle)] bg-[var(--sidebar-accent)] text-[var(--text-primary)] shadow-raised'
                        : 'text-[var(--text-muted)] hover:bg-[var(--sidebar-accent)]/60 hover:text-[var(--text-primary)]',
                    ].join(' ')}
                    aria-label={item.label}
                  >
                    <PixlieDoodle name={item.doodle} size="nav" className="opacity-85" />
                  </button>
                </Tooltip>
              );
            })}
          </div>
        )}
      </nav>

      {/* Avatar dock: collapsed = centered in rail; expanded = left row (not centered stack) */}
      <div className="mt-auto border-t border-[var(--border-subtle)]">
        {expanded ? (
          <div className="flex h-[5.25rem] items-center gap-1.5 px-1.5">
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="shrink-0"
              aria-label={`${nickname}, ${planLabel}. Open settings`}
            >
              <AvatarCircle sizeClass="h-8 w-8 text-xs" />
            </button>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[10px] font-medium leading-tight text-[var(--text-primary)]" title={nickname}>
                {nickname}
              </p>
              <p className="truncate text-[9px] text-[var(--text-muted)]">{planLabel}</p>
            </div>
            <button
              aria-label="Sign out"
              onClick={handleLogout}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--text-muted)] transition-colors hover:bg-[var(--danger-subtle)] hover:text-[var(--danger)]"
            >
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex h-[5.25rem] w-full items-center justify-center">
            <Tooltip side="right" content={`${nickname} · ${planLabel}`}>
              <button
                type="button"
                onClick={() => navigate('/settings')}
                className="grid place-items-center"
                aria-label={`${nickname}, ${planLabel}. Open settings`}
              >
                <AvatarCircle sizeClass="h-10 w-10 text-sm" />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    </aside>
  );
};

export default SidebarV2;
